import { useState, useCallback, useMemo, memo } from 'react'
import { Button, Input, Modal } from '@/shared/components/ui'
import type { Announcement, CreateAnnouncementRequest, UpdateAnnouncementRequest } from '../types'

interface AnnouncementFormProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: CreateAnnouncementRequest | UpdateAnnouncementRequest) => Promise<void>
  announcement?: Announcement
  availableHouses?: Array<{ id: string; name: string }>
  isLoading?: boolean
}

interface FormData {
  title: string
  content: string
  targetHouses: string[]
  priority: 'low' | 'medium' | 'high' | 'urgent'
  expirationDate: string
  isActive: boolean
}

const INITIAL_FORM_DATA: FormData = {
  title: '',
  content: '',
  targetHouses: [],
  priority: 'medium',
  expirationDate: '',
  isActive: true
}

const AnnouncementForm = memo<AnnouncementFormProps>(({
  isOpen,
  onClose,
  onSubmit,
  announcement,
  availableHouses = [],
  isLoading = false
}) => {
  // Initialize form data
  const [formData, setFormData] = useState<FormData>(() => {
    if (announcement) {
      return {
        title: announcement.title,
        content: announcement.content,
        targetHouses: announcement.targetHouses,
        priority: announcement.priority,
        expirationDate: announcement.expirationDate || '',
        isActive: announcement.isActive
      }
    }
    return INITIAL_FORM_DATA
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  // Memoized form title
  const formTitle = useMemo(() => 
    announcement ? 'Edit Announcement' : 'Create New Announcement', 
    [announcement]
  )

  // Reset form when modal opens/closes or announcement changes
  const resetForm = useCallback(() => {
    if (announcement) {
      setFormData({
        title: announcement.title,
        content: announcement.content,
        targetHouses: announcement.targetHouses,
        priority: announcement.priority,
        expirationDate: announcement.expirationDate || '',
        isActive: announcement.isActive
      })
    } else {
      setFormData(INITIAL_FORM_DATA)
    }
    setErrors({})
  }, [announcement])

  // Handle form field changes
  const handleChange = useCallback((field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }, [errors])

  // Handle house selection
  const handleHouseToggle = useCallback((houseId: string) => {
    setFormData(prev => ({
      ...prev,
      targetHouses: prev.targetHouses.includes(houseId)
        ? prev.targetHouses.filter(id => id !== houseId)
        : [...prev.targetHouses, houseId]
    }))
  }, [])

  // Validate form
  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required'
    } else if (formData.title.length > 100) {
      newErrors.title = 'Title must be less than 100 characters'
    }

    if (!formData.content.trim()) {
      newErrors.content = 'Content is required'
    } else if (formData.content.length > 1000) {
      newErrors.content = 'Content must be less than 1000 characters'
    }

    if (formData.expirationDate) {
      const expDate = new Date(formData.expirationDate)
      const now = new Date()
      if (expDate <= now) {
        newErrors.expirationDate = 'Expiration date must be in the future'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [formData])

  // Handle form submission
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return

    try {
      const submitData = {
        title: formData.title.trim(),
        content: formData.content.trim(),
        targetHouses: formData.targetHouses,
        priority: formData.priority,
        ...(formData.expirationDate && { expirationDate: formData.expirationDate }),
        ...(announcement && { isActive: formData.isActive })
      }

      await onSubmit(submitData)
      onClose()
      resetForm()
    } catch (error) {
      console.error('Failed to submit announcement:', error)
    }
  }, [formData, validateForm, onSubmit, onClose, resetForm, announcement])

  // Handle modal close
  const handleClose = useCallback(() => {
    onClose()
    resetForm()
  }, [onClose, resetForm])

  // Memoized minimum date for expiration
  const minDate = useMemo(() => {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    return tomorrow.toISOString().split('T')[0]
  }, [])

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={formTitle} size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Title */}
        <Input
          label="Title"
          value={formData.title}
          onChange={(e) => handleChange('title', e.target.value)}
          error={errors.title}
          placeholder="Enter announcement title"
          isRequired
          maxLength={100}
        />

        {/* Content */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Content <span className="text-red-500">*</span>
          </label>
          <textarea
            value={formData.content}
            onChange={(e) => handleChange('content', e.target.value)}
            placeholder="Enter announcement content"
            rows={4}
            maxLength={1000}
            className={`
              block w-full rounded-lg border-0 py-3 px-3 text-gray-900 shadow-sm 
              ring-1 ring-inset placeholder:text-gray-400 
              focus:ring-2 focus:ring-inset focus:ring-indigo-500
              transition-all duration-200 ease-in-out
              text-sm leading-6 resize-vertical
              ${errors.content ? 'ring-red-300 bg-red-50' : 'ring-gray-300 hover:ring-gray-400'}
            `}
          />
          {errors.content && (
            <p className="mt-1 text-sm text-red-600">{errors.content}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            {formData.content.length}/1000 characters
          </p>
        </div>

        {/* Priority */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Priority
          </label>
          <select
            value={formData.priority}
            onChange={(e) => handleChange('priority', e.target.value as FormData['priority'])}
            className="block w-full rounded-lg border-0 py-3 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-500 text-sm"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>

        {/* Target Houses */}
        {availableHouses.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target Houses
            </label>
            <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-200 rounded-lg p-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.targetHouses.length === availableHouses.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      handleChange('targetHouses', availableHouses.map(h => h.id))
                    } else {
                      handleChange('targetHouses', [])
                    }
                  }}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">All Houses</span>
              </label>
              <hr className="border-gray-200" />
              {availableHouses.map((house) => (
                <label key={house.id} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.targetHouses.includes(house.id)}
                    onChange={() => handleHouseToggle(house.id)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">{house.name}</span>
                </label>
              ))}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Leave empty to send to all houses
            </p>
          </div>
        )}

        {/* Expiration Date */}
        <Input
          label="Expiration Date (Optional)"
          type="date"
          value={formData.expirationDate}
          onChange={(e) => handleChange('expirationDate', e.target.value)}
          error={errors.expirationDate}
          min={minDate}
          helperText="Leave empty for no expiration"
        />

        {/* Active Status (only for editing) */}
        {announcement && (
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => handleChange('isActive', e.target.checked)}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
              Active announcement
            </label>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={isLoading}
            disabled={isLoading}
          >
            {announcement ? 'Update' : 'Create'} Announcement
          </Button>
        </div>
      </form>
    </Modal>
  )
})

AnnouncementForm.displayName = 'AnnouncementForm'

export default AnnouncementForm